import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { css } from '@emotion/react';

import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useReactToPrint } from 'react-to-print';

import Button from 'components/field/Button';
import CopyComponent from 'components/field/CopyComponent';
import OTPInput from 'components/field/OTPInput';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Skeleton from 'components/global/Skeleton';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';

import useAlert from 'hooks/useAlert';

import axios from 'services/axios';
import { MFA } from 'services/consts';

import { setUserDetails } from 'stores/userSlice';

import { faCopy, faDownload, faPrint } from '@fortawesome/pro-regular-svg-icons';

import * as S from './TwoFactorModal.styled';

const TwoFactorEnableModal = NiceModal.create(() => {
	const modal = useModal();
	const [value, setValue] = useState([null, null, null, null, null, null]);
	const [loading, setLoading] = useState(true);
	const { showError, showSuccess } = useAlert();
	const [qrCode, setQrCode] = useState('');
	const [secretKey, setSecretKey] = useState(null);
	const dispatch = useDispatch();
	const [recoveryCodes, setRecoveryCodes] = useState([]);
	const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
	const { t } = useTranslation();

	useEffect(() => {
		axios
			.post(MFA.ENABLE_MFA, {
				accessToken: localStorage.getItem('ACCESS_TOKEN')
			})
			.then((res) => {
				const { status, data } = res;
				if (status === 200) {
					setQrCode(data.qrCode);
					setSecretKey(data.secretKey);
					setLoading(false);
				} else {
					showError(data.code);
					modal.remove();
				}
			})
			.catch((error) => {
				showError({
					message: error.response?.data?.code
				});
				modal.remove();
			});
	}, []);

	const submit = () => {
		setLoading(true);
		const code = value.join('');
		axios
			.post(MFA.ACTIVATE_MFA, {
				activationCode: code
			})
			.then((res) => {
				const { status, data } = res;
				if (status === 200) {
					dispatch(setUserDetails({ mfaTypes: 'totp' }));
					setRecoveryCodes(data.recoveryCodes);
					setShowRecoveryCodes(true);
				} else {
					showError(data.code);
				}
			})
			.catch((error) => {
				showError({
					message: error.response?.data?.code
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const [isBackedUp, setIsBackedUp] = useState(false);

	const downloadBackupCodes = () => {
		const element = document.createElement('a');
		const file = new Blob([[t('twoFactorModal.backupCodesDownloadTitle'), ...recoveryCodes].join('\n')], {
			type: 'text/plain'
		});
		element.href = URL.createObjectURL(file);
		element.download = 'app.tryoto.com-backup-codes.txt';
		document.body.appendChild(element); // Required for this to work in FireFox
		element.click();
		setIsBackedUp(true);
		showSuccess({
			message: 'twoFactorModal.backupCodesDownloaded'
		});
	};

	const contentRef = useRef(null);
	const handlePrint = useReactToPrint({ contentRef });

	const printBackupCodes = () => {
		handlePrint();
		setIsBackedUp(true);
		showSuccess({
			message: 'twoFactorModal.backupCodesPrinted'
		});
	};

	const copyBackupCodes = () => {
		navigator.clipboard.writeText(recoveryCodes.join('\n'));
		setIsBackedUp(true);
		showSuccess({
			message: 'twoFactorModal.backupCodesCopied'
		});
	};

	return (
		<Modal maxWidth="small" className="modal-2FA" closeButton={!showRecoveryCodes || isBackedUp}>
			<ModalTitle>
				<Typography variant="p2" fontWeight="500">
					twoFactorModal.enable2FATitle
				</Typography>
			</ModalTitle>
			<ModalContent>
				{/*
				 * DON'T REMOVE THIS PRINTCONTAINER. THIS IS USED FOR PRINTING BACKUP CODES!
				 */}
				<S.PrintContainer ref={contentRef}>
					<Typography fontWeight="700">twoFactorModal.printDescription</Typography>
					{recoveryCodes.map((code, index) => (
						<Typography key={index} fontWeight="500">
							{code}
						</Typography>
					))}
				</S.PrintContainer>

				{showRecoveryCodes ? (
					<S.TwoFactorBackupCodesContainer>
						<Typography fontWeight="500" noWrap>
							twoFactorModal.regeneratedBackupCodesTabTitle
						</Typography>
						<Alert
							variant="warning"
							showIcon
							header="twoFactorModal.regeneratedBackupCodesTabAlertTitle"
							content="twoFactorModal.regeneratedBackupCodesTabAlertDescription"
						/>
						<S.BackupCodesContainer>
							<S.BackupCodesWrapper>
								{recoveryCodes.map((code, index) => (
									<Typography key={index} fontWeight="500">
										{code}
									</Typography>
								))}
							</S.BackupCodesWrapper>
							<S.BackupCodesButtons>
								<Button
									variant="transparent"
									label="download"
									icon={faDownload}
									size="large"
									onClick={downloadBackupCodes}
								/>
								<S.ButtonsSeparator />
								<Button
									variant="transparent"
									label="print"
									icon={faPrint}
									size="large"
									onClick={printBackupCodes}
								/>
								<S.ButtonsSeparator />
								<Button
									variant="transparent"
									label="copy"
									icon={faCopy}
									size="large"
									onClick={copyBackupCodes}
								/>
							</S.BackupCodesButtons>
						</S.BackupCodesContainer>
					</S.TwoFactorBackupCodesContainer>
				) : (
					<S.TwoFactorInstallContainer>
						<S.InstallStep>
							<S.StepNumber>
								<Typography variant="p2" fontWeight="500">
									1
								</Typography>
							</S.StepNumber>
							<S.StepContent>
								<Typography fontWeight="500" noWrap>
									<Trans
										i18nKey="twoFactorModal.enable2FAStep1"
										components={{
											androidLink: (
												<Typography
													component="a"
													href="https://play.google.com/store/search?q=authenticator%20app&c=apps"
													target="_blank"
													fontWeight="700"
													color="var(--blue)"
													style={{
														display: 'unset',
														textDecoration: 'none'
													}}
													noWrap
												/>
											),
											iosLink: (
												<Typography
													component="a"
													href="itms-apps://search.itunes.apple.com/WebObjects/MZSearch.woa/wa/search?media=software&term=authenticator%20app"
													target="_blank"
													fontWeight="700"
													color="var(--blue)"
													style={{
														display: 'unset',
														textDecoration: 'none'
													}}
													noWrap
												/>
											)
										}}
									/>
								</Typography>
							</S.StepContent>
						</S.InstallStep>
						<S.InstallStep>
							<S.StepNumber>
								<Typography variant="p2" fontWeight="500">
									2
								</Typography>
							</S.StepNumber>
							<S.StepContent>
								<Typography fontWeight="500" noWrap>
									twoFactorModal.enable2FAStep2
								</Typography>
								<S.QRCodeContainer>
									{loading ? (
										<Skeleton width="120px" height="120px" />
									) : (
										<Image
											url={`data:image/png;base64,${qrCode}`}
											alt="QR Code"
											height="120px"
											width="120px"
										/>
									)}
									<S.ManulEntryContainer>
										<FormItem label="twoFactorModal.manualEntry" isLoading={loading}>
											<CopyComponent value={secretKey} />
										</FormItem>
									</S.ManulEntryContainer>
								</S.QRCodeContainer>
							</S.StepContent>
						</S.InstallStep>
						<S.InstallStep>
							<S.StepNumber>
								<Typography variant="p2" fontWeight="500">
									3
								</Typography>
							</S.StepNumber>
							<S.StepContent>
								<Typography fontWeight="500" noWrap>
									twoFactorModal.enable2FAStep3
								</Typography>
								<OTPInput
									value={value}
									setValue={setValue}
									loading={loading}
									length={6}
									onFilledOnce={submit}
								/>
							</S.StepContent>
						</S.InstallStep>
					</S.TwoFactorInstallContainer>
				)}
			</ModalContent>
			<ModalActions>
				{!showRecoveryCodes && (
					<Button
						variant="blackOutline"
						label="cancel"
						onClick={modal.remove}
						css={css`
							text-decoration: underline;
							border: none !important;
						`}
					/>
				)}
				{!showRecoveryCodes && (
					<Button
						label="verify"
						onClick={submit}
						disabled={value.includes(null) || loading}
						loading={loading}
					/>
				)}
				{showRecoveryCodes && isBackedUp && <Button label="twoFactorModal.done" onClick={modal.remove} />}
				{showRecoveryCodes && !isBackedUp && (
					<Typography fontWeight="500" fullWidth align="center" className="py-3">
						twoFactorModal.firstlyBackupYourCodes
					</Typography>
				)}
			</ModalActions>
		</Modal>
	);
});

export default TwoFactorEnableModal;
